import React from "react";
import styled from "styled-components";
import DefaultButton from "../../../atoms/Buttons/Button/Button";
import Whitespace from "../../../../../../ProjectNonSpecificComponents/Elements/Whitespace";
import { TAppInfoModalType } from "./Interfaces";
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv";
import Icon from "../../../atoms/Icons/Icon/Icon";
import { TIconKey } from "../../../atoms/Icons/Icon/Interfaces";
import useScreenType from 'components/ProjectNonSpecificComponents/Hooks/useScreenType';
import Overlay from 'components/ProjectNonSpecificComponents/Elements/Overlay/Overlay';

interface Props {
  type: TAppInfoModalType;
  title: string;
  description?: string;
  cancelText?: string;
  proceedText?: string;
  hideCancelBtn?: boolean;
  /**
   * Starts fadeout animation with a default duration of 1s.
   */
  hideModalWithFadeout?: boolean;
  onCancel?: () => void;
  onProceed?: () => void;
  onFadeoutFinish?: () => void;
}

const InfoModal = ({
  type,
  title,
  description,
  cancelText,
  proceedText,
  hideCancelBtn = false,
  hideModalWithFadeout = false,
  onCancel,
  onProceed,
  onFadeoutFinish,
}: Props) => {
  const iconKey: TIconKey | null =
    type == "fail"
      ? "error-sign"
      : type == "warning"
      ? "warning-sign"
      : type == "info"
      ? "info-sign"
      : type == "success"
      ? null
      : null;
  const { isMobile } = useScreenType()

  return (
    <Overlay
      zIndex={120}
      screenCentered
      entranceFliesUpAnimated
      hideOverlayWithFadeout={hideModalWithFadeout}
      onFadeoutFinish={onFadeoutFinish}
    >
      <ModalBase>
        <ContentContainer
          className="center-y"
          style={{
            width: isMobile ? '94vw' : 600,
            borderTop: `8px solid var(--${type})`,
          }}
        >
          <Whitespace h x={isMobile ? '1' : '2'} />
          <Icon iconKey={iconKey} />
          <Whitespace h x={isMobile ? '1' : '2'} />
          <HyperDiv
            direction="column"
            style={{
              overflow: 'hidden',
            }}
          >
            <div
              className="hide-overflown-text-wrap"
              style={{
                fontSize: isMobile ? 16 : 21,
              }}
            >
              {title}
            </div>
            <Whitespace x=".5" />
            <ContentText
              style={{
                fontSize: isMobile ? 12 : 16,
              }}
            >
              {description}
            </ContentText>
          </HyperDiv>
          <Whitespace h x={isMobile ? '1' : '2'} />
        </ContentContainer>

        <ButtonsContainer className="center-y-end">
          {!hideCancelBtn && (
            <DefaultButton
              style={{ minWidth: 130 }}
              inverted
              onClick={onCancel}
              children={cancelText ?? "Cancel"}
            />
          )}
          <Whitespace h x=".75" />
          <DefaultButton
            style={{ minWidth: 130 }}
            onClick={onProceed}
            children={proceedText ?? "Proceed"}
          />
        </ButtonsContainer>
      </ModalBase>
    </Overlay>
  );
};

export default InfoModal;

const ModalBase = styled.div`
  background-color: var(--white);
  border-radius: 3px;
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const ContentContainer = styled.div`
  padding: 24px 0;
`;

const ButtonsContainer = styled.div`
  height: 64px;

  padding: 0 16px;
  border-top: 1px solid var(--classic-gray);
`;

const ContentText = styled.div`
  line-height: 1.1875em;
`;
